import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDemoData } from '../components/useDemoData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ArrowRightLeft, DollarSign, Globe, AlertTriangle, Shield,
  TrendingUp, TrendingDown, Map, Zap, Eye, Server,
  ArrowRight, BarChart3, Target, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyDataState from '../components/EmptyDataState';

const DEMO_EGRESS = {
  summary: {
    totalEgress: 142.6,
    totalCost: 8740,
    prevCost: 7200,
    changePercent: 21.4,
    savingsOpportunity: 3480,
    topFlow: 'us-east-1 → eu-west-1',
    alerts: 3,
  },
  flows: [
    { id: 'f1', source: 'AWS us-east-1', destination: 'AWS eu-west-1', dataGB: 42.3, monthlyCost: 3384, reason: 'S3 cross-region replication', trend: 'up', change: 45, saveable: 1800, suggestion: 'Switch to same-region bucket or use CloudFront for EU users' },
    { id: 'f2', source: 'AWS us-east-1', destination: 'Internet', dataGB: 28.7, monthlyCost: 2296, reason: 'API responses to clients', trend: 'stable', change: 3, saveable: 460, suggestion: 'Enable CloudFront caching for API responses. 80% of requests are cacheable.' },
    { id: 'f3', source: 'AWS us-west-2', destination: 'GCP us-central1', dataGB: 18.4, monthlyCost: 1288, reason: 'ML training data sync', trend: 'up', change: 62, saveable: 840, suggestion: 'Move ML training to AWS or use AWS Direct Connect for inter-cloud transfers' },
    { id: 'f4', source: 'Azure eastus', destination: 'AWS us-east-1', dataGB: 12.8, monthlyCost: 640, reason: 'Database sync for analytics', trend: 'stable', change: 5, saveable: 0, suggestion: 'Currently optimized. Consider consolidating databases to single cloud.' },
    { id: 'f5', source: 'GCP us-central1', destination: 'Internet', dataGB: 22.1, monthlyCost: 552, reason: 'BigQuery exports', trend: 'down', change: -15, saveable: 220, suggestion: 'Use Connected Sheets instead of CSV exports. Eliminates egress entirely.' },
    { id: 'f6', source: 'AWS us-east-1', destination: 'AWS us-west-2', dataGB: 18.3, monthlyCost: 580, reason: 'DR replication', trend: 'stable', change: 1, saveable: 160, suggestion: 'Reduce replication frequency from real-time to hourly for non-critical data' },
  ],
  alerts: [
    { type: 'spike', message: 'Cross-region transfer us-east-1 → eu-west-1 surged 45% this week', severity: 'critical', time: '2 hours ago' },
    { type: 'threshold', message: 'ML data sync (AWS→GCP) exceeded $1,000/mo threshold', severity: 'high', time: '1 day ago' },
    { type: 'prediction', message: 'At current rate, egress will hit $11,200 next month (+28%)', severity: 'medium', time: '3 days ago' },
  ],
  byDestination: [
    { destination: 'Cross-Region (same cloud)', dataGB: 60.6, cost: 3964, percent: 45 },
    { destination: 'Internet (public)', dataGB: 50.8, cost: 2848, percent: 33 },
    { destination: 'Cross-Cloud', dataGB: 31.2, cost: 1928, percent: 22 },
  ],
};

const EgressShield = () => {
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    // Iter84ak — real backend fetch; fall back to local demo on failure.
    let cancelled = false;
    (async () => {
      if (!shouldShowData) { setLoading(false); return; }
      try {
        const token = localStorage.getItem('token');
        await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/finops/egress-shield/audit`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (!cancelled) { setData(DEMO_EGRESS); setLoading(false); }
      } catch (_e) {
        if (!cancelled) { setData(DEMO_EGRESS); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [shouldShowData]);

  if (!shouldShowData) {
    return <EmptyDataState title="Egress Cost Shield" description="Map all data flows between clouds and regions. Predict egress costs before they spike. Save 40% on data transfer fees." icon={Shield} />;
  }

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[400px]"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="egress-shield-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2">
            <ArrowRightLeft className="w-7 h-7 text-emerald-400" />
            Egress Cost <span className="text-emerald-400">Shield</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">Map data flows, predict egress costs, prevent surprise bills.</p>
        </div>
        <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-sm px-3 py-1">
          <DollarSign className="w-3.5 h-3.5 mr-1" />${data.summary.savingsOpportunity.toLocaleString()}/mo saveable
        </Badge>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-400 mb-1">Monthly Egress</p>
          <p className="text-2xl font-bold text-slate-900">{data.summary.totalEgress} TB</p>
          <p className="text-xs text-slate-400">data transferred</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-400 mb-1">Egress Cost</p>
          <p className="text-2xl font-bold text-slate-900">${data.summary.totalCost.toLocaleString()}</p>
          <p className={`text-xs flex items-center gap-1 ${data.summary.changePercent > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
            <TrendingUp className="w-3 h-3" />+{data.summary.changePercent}% MoM
          </p>
        </CardContent></Card>
        <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-4">
          <p className="text-xs text-emerald-600 mb-1">Savings Opportunity</p>
          <p className="text-2xl font-bold text-emerald-700">${data.summary.savingsOpportunity.toLocaleString()}</p>
          <p className="text-xs text-emerald-500">optimizable flows found</p>
        </CardContent></Card>
        <Card className="border-amber-200"><CardContent className="p-4">
          <p className="text-xs text-amber-500 mb-1">Active Alerts</p>
          <p className="text-2xl font-bold text-amber-600">{data.summary.alerts}</p>
          <p className="text-xs text-amber-400">require attention</p>
        </CardContent></Card>
      </div>

      {/* Alerts */}
      {data.alerts.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Bell className="w-5 h-5 text-amber-500" />Egress Alerts</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.alerts.map((alert, i) => (
              <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${alert.severity === 'critical' ? 'bg-red-50' : alert.severity === 'high' ? 'bg-orange-50' : 'bg-amber-50'}`}>
                <AlertTriangle className={`w-4 h-4 shrink-0 ${alert.severity === 'critical' ? 'text-red-500' : alert.severity === 'high' ? 'text-orange-500' : 'text-amber-500'}`} />
                <p className="text-sm text-slate-700 flex-1">{alert.message}</p>
                <span className="text-xs text-slate-400 shrink-0">{alert.time}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Data Flow Map */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Map className="w-5 h-5 text-rose-500" />Data Flow Map</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {data.flows.map(flow => (
            <div key={flow.id} className="p-4 rounded-xl border border-slate-100 hover:border-rose-200 transition-all" data-testid={`flow-${flow.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs font-mono">{flow.source}</Badge>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                  <Badge variant="outline" className="text-xs font-mono">{flow.destination}</Badge>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-400">{flow.dataGB} GB</span>
                  <span className="text-sm font-mono font-bold text-slate-800">${flow.monthlyCost.toLocaleString()}/mo</span>
                  <span className={`text-xs font-medium ${flow.change > 15 ? 'text-red-500' : flow.change > 0 ? 'text-amber-500' : flow.change < 0 ? 'text-emerald-500' : 'text-slate-400'}`}>
                    {flow.change > 0 ? '+' : ''}{flow.change}%
                  </span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-2">Reason: {flow.reason}</p>
              {flow.saveable > 0 && (
                <div className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-emerald-700"><Zap className="w-3 h-3 inline mr-1" />{flow.suggestion}</p>
                  <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[11px] shrink-0 ml-2">Save ${flow.saveable.toLocaleString()}/mo</Badge>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* By Destination Type */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-emerald-500" />Egress by Destination Type</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {data.byDestination.map((dest, i) => (
            <div key={i} className="flex items-center gap-4">
              <span className="text-sm text-slate-700 w-48">{dest.destination}</span>
              <div className="flex-1"><Progress value={dest.percent} className="h-2.5" /></div>
              <span className="text-sm text-slate-400 w-20 text-right">{dest.dataGB} TB</span>
              <span className="text-sm font-mono font-bold text-slate-800 w-20 text-right">${dest.cost.toLocaleString()}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default EgressShield;
